import { useEffect, useState } from 'react';
import { doRequest } from '../../../utils/http';
import {
  DEFAULT_REQUESTOR,
  DEFAULT_TENANT,
} from '../../../../constants/configs';

const API_BASE_URL = 'https://url.prod.merch.vpsvc.com/v3';
const AVAILABILITY_API_BASE_URL =
  'https://page-availability-service.prod.merch.vpsvc.com/api/v2';
const DISCOVERY_API_BASE_URL =
  'https://discovery-pages.large-assortment.vpsvc.com/v1/discoverypageequivalents';

// NOTE: The Actual Route types could be found here https://url.prod.merch.vpsvc.com/v3/types/vistaprint?requestor=bookends
// But for now we are using the following types because page availability not supporting all the types

// The page type
// Available values : ProductPage, CategoryPage, HomePage, MarketingLandingPage, StaticPage, ConsumerDetailPage
const ROUTE_TYPE: any = {
  Category: 'CategoryPage',
  Product: 'ProductPage',
  Other: 'HomePage',
  Static: 'StaticPage',
  SpecialOffers: 'SpecialOffersPage',
  Offer: 'SpecialOffersPage',
  MarketingLanding: 'MarketingLandingPage',
  '': 'HomePage',
};

const handleRequest = async (
  url: string,
  locale: string,
  type: string,
  tenant: string
) => {
  const res = await doRequest(
    `${API_BASE_URL}/${type}/${tenant}/${locale}/${url}?requestor=${DEFAULT_REQUESTOR}`
  );
  return res;
};

const urlValidation = async (
  id: string,
  type: string,
  locale: string,
  tenant: string
) => {
  const availableRoutes = await doRequest(
    `${AVAILABILITY_API_BASE_URL}/${tenant}/${type}/url/${locale}`
  );
  const findRoute = availableRoutes.find((route: any) => route.urlId === id);
  if (!findRoute) {
    throw new Error('URL is not active');
  }
  return;
};

const handleDiscoveryPage = async (
  discoveryId: string,
  locale: string,
  tenant: string
) => {
  const data = await doRequest(
    `${DISCOVERY_API_BASE_URL}/${tenant}/${discoveryId}?requestor=${DEFAULT_REQUESTOR}`
  );
  if (data?.[locale]) {
    return true;
  }
  throw new Error('URL is not active');
};

export const getRedirectURL = async ({
  currentUrl,
  requestLocale,
  locale,
  tenant = DEFAULT_TENANT,
}: {
  currentUrl: string;
  requestLocale: string;
  locale: string;
  tenant?: string;
}) => {
  try {
    if (currentUrl === '/search') {
      return `/search${window.location.search}`;
    }
    const encodedURL = encodeURIComponent(currentUrl);
    const data = await handleRequest(encodedURL, locale, 'id', tenant);
    const requestURL = await handleRequest(
      data.id,
      requestLocale,
      'url',
      tenant
    );
    let type = requestURL.type;
    if (data.id === 'other:offers') {
      type = 'Offer';
    }
    if (ROUTE_TYPE[type] === 'DiscoveryPage') {
      await handleDiscoveryPage(data.id, requestLocale.toLowerCase(), tenant);
    } else {
      if (ROUTE_TYPE[type]) {
        await urlValidation(
          data.id,
          ROUTE_TYPE[type],
          requestLocale.toLowerCase(),
          tenant
        );
        return requestURL.url;
      }
    }
    return '/';
  } catch (err) {
    return '/';
  }
};

const usePageAvailability = (
  currentUrl: string,
  locale: string,
  requestLocale?: string,
  tenant: string = DEFAULT_TENANT
) => {
  const [redirectURL, setRedirectURL] = useState('/');

  useEffect(() => {
    if (!requestLocale) {
      setRedirectURL('/');
      return;
    }
    (async () => {
      const url = await getRedirectURL({
        currentUrl,
        requestLocale,
        locale,
        tenant,
      });
      if (url !== '/') {
        setRedirectURL(url);
        return;
      }
      const split_url = currentUrl.split('/');
      if (split_url?.length >= 1) {
        const url = await getRedirectURL({
          currentUrl: '/' + split_url[1],
          requestLocale,
          locale,
          tenant,
        });
        setRedirectURL(url);
        return;
      }
      setRedirectURL('/');
    })();
  }, [requestLocale, locale, currentUrl, tenant]);

  return redirectURL;
};

export default usePageAvailability;
